
import { offlineDb } from '@/db/offlineDb';
import { ShoppingList, ShoppingItem } from '@/types';
import OfflineLogger from '@/utils/offlineLogger';

// ✅ FIX: Aggiungiamo un lock per prevenire creazioni multiple parallele
const creationLocks = new Map<string, Promise<ShoppingList | null>>();

export class PrimaryListOfflineService {
  async getPrimaryListOffline(userId: string): Promise<ShoppingList | null> {
    try {
      // Retrieve all lists for the given user that are not marked as deleted
      // and find the first one whose `is_primary` flag is truthy.
      const candidateLists = await offlineDb.shoppingLists
        .where('user_id')
        .equals(userId)
        .and(list => list._syncStatus !== 'deleted')
        .toArray();

      const existingPrimaryList = candidateLists.find(
        list => list.is_primary === true
      );

      if (existingPrimaryList) {
        OfflineLogger.info('PrimaryListOfflineService', 'Found existing primary list in IndexedDB');

        // Fetch associated items that are not marked as deleted
        const itemRecords = await offlineDb.shoppingItems
          .where('list_id')
          .equals(existingPrimaryList.id)
          .and(item => item._syncStatus !== 'deleted')
          .toArray();

        const items: ShoppingItem[] = itemRecords.map(record => ({
          id: record.id,
          list_id: record.list_id,
          product_id: record.product_id,
          quantity: record.quantity || '1',
          unit: record.unit || '',
          category: record.category || '',
          checked: record.checked === true,
          position: record.position,
          attributes: record.attributes || [],
          created_at: new Date(record.created_at || Date.now()),
          user_id: record.user_id
        }));

        return this.convertToShoppingList(existingPrimaryList, items);
      }

      return null;
    } catch (error) {
      OfflineLogger.error('PrimaryListOfflineService.getPrimaryListOffline', error);
      return null;
    }
  }

  async ensurePrimaryListExists(userId: string): Promise<string | null> {
    try {
      const id = userId || 'offline-user';

      // ✅ FIX: Controllo se esiste già una lista primaria
      const existingList = await this.getPrimaryListOffline(id);
      if (existingList) {
        OfflineLogger.info('PrimaryListOfflineService', `Primary list already exists: ${existingList.id}`);
        return existingList.id;
      }

      // ✅ FIX: Usa un lock per prevenire creazioni parallele
      const lockKey = `create_primary_${id}`;
      if (creationLocks.has(lockKey)) {
        OfflineLogger.info('PrimaryListOfflineService', 'Primary list creation already in progress, waiting...');
        const result = await creationLocks.get(lockKey);
        return result?.id || null;
      }

      // Crea il lock e procede con la creazione
      const creationPromise = this.createPrimaryListSafe(id);
      creationLocks.set(lockKey, creationPromise);

      try {
        const result = await creationPromise;
        return result?.id || null;
      } finally {
        // Rimuove il lock una volta completata la creazione
        creationLocks.delete(lockKey);
      }
    } catch (error) {
      OfflineLogger.error('PrimaryListOfflineService.ensurePrimaryListExists', error);
      return null;
    }
  }

  private async createPrimaryListSafe(userId: string): Promise<ShoppingList | null> {
    try {
      OfflineLogger.info('PrimaryListOfflineService', '🔄 Tentativo di creazione lista primaria...');
      
      // ✅ FIX: Doppio controllo per evitare race condition
      const doubleCheck = await this.getPrimaryListOffline(userId);
      if (doubleCheck) {
        OfflineLogger.info('PrimaryListOfflineService', 'Primary list found during double check, aborting creation');
        return doubleCheck;
      }

      OfflineLogger.info('PrimaryListOfflineService', '✅ Creazione lista primaria in corso...');
      
      const newPrimaryList = {
        id: crypto.randomUUID(),
        name: 'Lista della spesa',
        user_id: userId,
        is_primary: true,
        rapid_insert: false,
        shop_id: null,
        background_gradient: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        _syncStatus: 'pending' as const,
        _lastModified: Date.now()
      };

      await offlineDb.shoppingLists.add(newPrimaryList);
      OfflineLogger.info('PrimaryListOfflineService', `✅ Created new primary list: ${newPrimaryList.id}`);
      
      return this.convertToShoppingList(newPrimaryList);
    } catch (error) {
      OfflineLogger.error('PrimaryListOfflineService.createPrimaryListSafe', error);
      return null;
    }
  }

  private convertToShoppingList(syncableList: any, items: ShoppingItem[] = []): ShoppingList {
    return {
      id: syncableList.id,
      name: syncableList.name,
      user_id: syncableList.user_id,
      isPrimary: syncableList.is_primary || false,
      is_primary: syncableList.is_primary || false,
      rapid_insert: syncableList.rapid_insert || false,
      shop_id: syncableList.shop_id,
      backgroundGradient: syncableList.background_gradient,
      background_gradient: syncableList.background_gradient,
      createdAt: typeof syncableList.created_at === 'string' 
        ? new Date(syncableList.created_at) 
        : syncableList.created_at || new Date(),
      updatedAt: typeof syncableList.updated_at === 'string'
        ? new Date(syncableList.updated_at)
        : syncableList.updated_at || new Date(),
      items,
      collaborators: []
    };
  }
}

export const primaryListOfflineService = new PrimaryListOfflineService();
